import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { decodeSensorHeatmapRaster } from "../../utils/sensorHeatmapRasterUtils";

const rasterToBlob = ({ width, height, pixels }) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Impossibile visualizzare il raster heatmap");
  const image = context.createImageData(width, height);
  image.data.set(pixels);
  context.putImageData(image, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      canvas.width = 0;
      canvas.height = 0;
      if (blob) resolve(blob);
      else reject(new Error("Impossibile decodificare l'immagine heatmap"));
    }, "image/png");
  });
};

const SensorHeatmapLayer = ({ data, opacity = 0.7, onStatusChange }) => {
  const map = useMap();
  const layerRef = useRef(null);
  const opacityRef = useRef(opacity);

  useEffect(() => {
    opacityRef.current = opacity;
    layerRef.current?.setOpacity(opacity);
  }, [opacity]);

  useEffect(() => {
    const controller = new AbortController();
    let isDisposed = false;
    let objectUrl;
    let layer;
    const report = (status, error = null) => {
      if (!isDisposed)
        onStatusChange?.({ requestId: data.requestId, status, error });
    };
    const disposeLayer = () => {
      if (layer) {
        map.removeLayer(layer);
        layer.off();
        layer = null;
        layerRef.current = null;
      }
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    const setupLayer = async () => {
      report("loading");
      try {
        const raster = await decodeSensorHeatmapRaster(
          data.raster_base64,
          controller.signal,
        );
        if (isDisposed) return;
        const blob = await rasterToBlob(raster);
        if (isDisposed) return;
        const [minX, minY, maxX, maxY] = raster.boundingBox;
        const bounds = L.latLngBounds(
          L.CRS.EPSG3857.unproject(L.point(minX, minY)),
          L.CRS.EPSG3857.unproject(L.point(maxX, maxY)),
        );
        const paneName = "sensor-heatmap-pane";
        const pane = map.getPane(paneName) || map.createPane(paneName);
        pane.style.zIndex = "375";
        pane.style.pointerEvents = "none";
        objectUrl = window.URL.createObjectURL(blob);
        layer = L.imageOverlay(objectUrl, bounds, {
          pane: paneName,
          opacity: opacityRef.current,
          interactive: false,
        });
        layer.on("load", () => report("ready"));
        layer.on("error", () => {
          disposeLayer();
          report("error", "Impossibile visualizzare l'immagine heatmap");
        });
        layerRef.current = layer;
        layer.addTo(map);
      } catch (error) {
        disposeLayer();
        report(
          "error",
          "Impossibile decodificare la heatmap. Premi Aggiorna per riprovare.",
        );
      }
    };
    setupLayer();
    return () => {
      isDisposed = true;
      controller.abort();
      disposeLayer();
    };
  }, [data, map, onStatusChange]);

  return null;
};

export default SensorHeatmapLayer;
